import { useCallback, useEffect, useMemo, useState } from "react";

import { createNews, deleteNews, fetchNews, updateNews, uploadAdminMedia } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminArticle } from "@/features/admin/types";

import PaginationControls from "./PaginationControls";

const categories = ["Club", "Football", "Basketball", "Volleyball", "Natation", "Culture", "Événement"];
const defaultArticleImage =
  "https://readdy.ai/api/search-image?query=senegal%20africa%20news%20article%20professional%20photography%20editorial%20clean%20background&width=400&height=300&seq=admnews1&orientation=landscape";
const ARTICLES_PER_PAGE = 4;

export default function ActualitesTab() {
  const { request } = useAdminSession();
  const [articles, setArticles] = useState<AdminArticle[]>([]);
  const [filter, setFilter] = useState<"tous" | "publie" | "brouillon" | "archive">("tous");
  const [showForm, setShowForm] = useState(false);
  const [editArticle, setEditArticle] = useState<AdminArticle | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageMode, setImageMode] = useState<"url" | "upload">("url");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [form, setForm] = useState({
    titre: "",
    categorie: "Club",
    extrait: "",
    contenu: "",
    image: "",
    featured: false,
    statut: "brouillon" as "publie" | "brouillon"
  });

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextArticles = await fetchNews(request);
      setArticles(nextArticles);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les actualités";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadArticles();
  }, [loadArticles]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const openNew = () => {
    setEditArticle(null);
    setForm({ titre: "", categorie: "Club", extrait: "", contenu: "", image: "", featured: false, statut: "brouillon" });
    setImageMode("url");
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageInputKey((current) => current + 1);
    setShowForm(true);
  };

  const openEdit = (article: AdminArticle) => {
    setEditArticle(article);
    setForm({
      titre: article.titre,
      categorie: article.categorie,
      extrait: article.extrait,
      contenu: article.contenu,
      image: article.image ?? "",
      featured: article.featured,
      statut: article.statut === "archive" ? "brouillon" : article.statut
    });
    setImageMode("url");
    setSelectedImageFile(null);
    setImagePreviewUrl(article.image ?? null);
    setImageInputKey((current) => current + 1);
    setShowForm(true);
  };

  useEffect(() => {
    if (!selectedImageFile) {
      if (!showForm) {
        setImagePreviewUrl(null);
      }
      return;
    }

    const preview = URL.createObjectURL(selectedImageFile);
    setImagePreviewUrl(preview);

    return () => {
      URL.revokeObjectURL(preview);
    };
  }, [selectedImageFile, showForm]);

  const handleSave = async () => {
    if (!form.titre.trim() || !form.extrait.trim() || !form.contenu.trim()) {
      return;
    }

    try {
      let resolvedImage = form.image.trim() || editArticle?.image || defaultArticleImage;

      if (imageMode === "upload" && selectedImageFile) {
        const [asset] = await uploadAdminMedia(request, [selectedImageFile], "asc-sonatel/news");
        resolvedImage = asset?.url ?? resolvedImage;
      }

      if (editArticle) {
        await updateNews(request, editArticle.id, {
          titre: form.titre,
          categorie: form.categorie,
          extrait: form.extrait,
          contenu: form.contenu,
          featured: form.featured,
          statut: form.statut,
          image: resolvedImage
        });
        showToast("Article mis à jour");
      } else {
        await createNews(request, {
          titre: form.titre,
          categorie: form.categorie,
          extrait: form.extrait,
          contenu: form.contenu,
          featured: form.featured,
          statut: form.statut,
          image: resolvedImage
        });
        showToast("Article créé avec succès");
      }

      setShowForm(false);
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      setImageInputKey((current) => current + 1);
      await loadArticles();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setError(message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNews(request, id);
      showToast("Article supprimé");
      await loadArticles();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    }
  };

  const handleToggleStatus = async (article: AdminArticle) => {
    const nextStatus = article.statut === "publie" ? "archive" : "publie";

    try {
      await updateNews(request, article.id, { statut: nextStatus });
      showToast(nextStatus === "publie" ? "Article publié" : "Article archivé");
      await loadArticles();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Action impossible";
      setError(message);
    }
  };

  const filtered = useMemo(() => {
    return articles.filter((article) => filter === "tous" || article.statut === filter);
  }, [articles, filter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / ARTICLES_PER_PAGE));
  const paginatedArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ARTICLES_PER_PAGE);
  }, [currentPage, filtered]);

  const statusConfig: Record<AdminArticle["statut"], { label: string; cls: string }> = {
    publie: { label: "Publié", cls: "bg-emerald-100 text-emerald-600" },
    brouillon: { label: "Brouillon", cls: "bg-amber-100 text-amber-600" },
    archive: { label: "Archivé", cls: "bg-gray-100 text-gray-500" }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg bg-emerald-500 text-white text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-lg"></i>
          {toast}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Gestion des Actualités</h1>
          <p className="text-gray-500 text-sm mt-1">Publiez et modérez les articles du club</p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors whitespace-nowrap self-start sm:self-auto"
        >
          <i className="ri-add-line"></i>
          Nouvel article
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {(["tous", "publie", "brouillon", "archive"] as const).map((value) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              filter === value ? "bg-[#E8521A] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-[#E8521A] hover:text-[#E8521A]"
            }`}
          >
            {value === "tous" ? "Tous" : value === "publie" ? "Publiés" : value === "brouillon" ? "Brouillons" : "Archivés"}
            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${filter === value ? "bg-white/20" : "bg-gray-100"}`}>
              {value === "tous" ? articles.length : articles.filter((article) => article.statut === value).length}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl border border-gray-100 h-40 animate-pulse"></div>
          ))
        ) : paginatedArticles.map((article) => {
          const status = statusConfig[article.statut];

          return (
            <div key={article.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-colors">
              <div className="flex gap-4 p-4">
                <img src={article.image ?? defaultArticleImage} alt={article.titre} className="w-24 h-20 rounded-xl object-cover flex-shrink-0 object-top" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${status.cls}`}>{status.label}</span>
                    {article.featured ? (
                      <span className="text-xs bg-orange-100 text-[#E8521A] px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                        <i className="ri-star-line mr-1"></i>À la une
                      </span>
                    ) : null}
                  </div>
                  <h3 className="text-sm font-bold text-[#1A1A1A] line-clamp-2 mb-1">{article.titre}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="bg-gray-100 px-2 py-0.5 rounded-full">{article.categorie}</span>
                    <span>{article.date}</span>
                  </div>
                </div>
              </div>
              <div className="px-4 pb-4 flex items-center justify-between border-t border-gray-50 pt-3">
                <p className="text-xs text-gray-500 line-clamp-1 flex-1 mr-4">{article.extrait}</p>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => void handleToggleStatus(article)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                      article.statut === "publie"
                        ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                        : "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
                    }`}
                    title={article.statut === "publie" ? "Archiver" : "Publier"}
                  >
                    <i className={`${article.statut === "publie" ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
                  </button>
                  <button
                    onClick={() => openEdit(article)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-[#E8521A] transition-colors"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => void handleDelete(article.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && filtered.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="actualités"
        />
      ) : null}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editArticle ? "Modifier l'article" : "Nouvel article"}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre *</label>
                <input
                  type="text"
                  value={form.titre}
                  onChange={(event) => setForm({ ...form, titre: event.target.value })}
                  placeholder="Titre de l'article..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Catégorie</label>
                  <select
                    value={form.categorie}
                    onChange={(event) => setForm({ ...form, categorie: event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                  >
                    {categories.map((category) => <option key={category}>{category}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Statut</label>
                  <select
                    value={form.statut}
                    onChange={(event) => setForm({ ...form, statut: event.target.value as "publie" | "brouillon" })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                  >
                    <option value="brouillon">Brouillon</option>
                    <option value="publie">Publier</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Extrait</label>
                <textarea
                  value={form.extrait}
                  onChange={(event) => setForm({ ...form, extrait: event.target.value })}
                  placeholder="Résumé court de l'article..."
                  rows={2}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors resize-none"
                />
                <p className="mt-1 text-xs text-gray-400">Résumé court affiché dans les cartes d’actualité.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Image de l&apos;article</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setImageMode("url")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      imageMode === "url" ? "bg-[#E8521A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    URL HTTP
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageMode("upload")}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      imageMode === "upload" ? "bg-[#E8521A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Choisir sur l&apos;ordinateur
                  </button>
                </div>

                {imageMode === "url" ? (
                  <>
                    <input
                      type="url"
                      value={form.image}
                      onChange={(event) => {
                        setForm({ ...form, image: event.target.value });
                        setImagePreviewUrl(event.target.value || null);
                      }}
                      placeholder="https://..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    />
                    <p className="text-xs text-gray-400 mt-1">Tu peux coller une URL distante si tu préfères.</p>
                  </>
                ) : (
                  <>
                    <input
                      key={imageInputKey}
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0] ?? null;
                        setSelectedImageFile(file);
                        if (file) {
                          setForm({ ...form, image: "" });
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-xl file:border-0 file:bg-orange-50 file:px-4 file:py-2.5 file:text-sm file:font-medium file:text-[#E8521A] hover:file:bg-orange-100"
                    />
                    <p className="text-xs text-gray-400 mt-1">L&apos;image sera envoyée sur Cloudinary au moment de publier.</p>
                  </>
                )}
              </div>
              {imagePreviewUrl ? (
                <div className="rounded-xl overflow-hidden border border-gray-100">
                  <img src={imagePreviewUrl} alt="Aperçu" className="w-full h-40 object-cover" />
                </div>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Contenu</label>
                <textarea
                  value={form.contenu}
                  onChange={(event) => setForm({ ...form, contenu: event.target.value })}
                  placeholder="Contenu complet de l'article..."
                  rows={12}
                  className="min-h-72 w-full resize-y rounded-xl border border-gray-200 px-4 py-3 text-sm leading-7 transition-colors focus:border-[#E8521A] focus:outline-none"
                />
                <div className="mt-1 flex flex-col gap-1 text-xs text-gray-400 sm:flex-row sm:items-center sm:justify-between">
                  <span>Texte complet sans limite courte. Les retours à la ligne seront conservés à la lecture.</span>
                  <span>{form.contenu.length.toLocaleString("fr-FR")} caractères</span>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => setForm({ ...form, featured: !form.featured })}
                  className={`w-11 h-6 rounded-full transition-colors relative ${form.featured ? "bg-[#E8521A]" : "bg-gray-200"}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.featured ? "left-6" : "left-1"}`}></div>
                </div>
                <span className="text-sm text-gray-700">Mettre à la une</span>
              </label>
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button onClick={() => void handleSave()} className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors">
                {editArticle ? "Mettre à jour" : "Publier"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
